import React, { useState, useRef, useEffect } from 'react';
import { IMAGE_BLURS } from '../data/imageBlurs';

interface BenefitCardProps {
  title: string;
  headline: string;
  body: string;
  gradient: string;
  isOpen: boolean;
  onToggle: () => void;
  image?: string;
  imageBlur?: string;
  imageMobile?: string;
}

const BenefitCard: React.FC<BenefitCardProps> = ({ title, headline, body, gradient, image, imageBlur, imageMobile, isOpen, onToggle }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);


  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete) {
      if (img.naturalWidth > 0) {
        setImageLoaded(true);
      }
    }
  }, []);

  return (
    <div
      className={`
        relative flex flex-col justify-end p-8 md:p-10
        w-[calc(100vw-48px)] md:w-[480px] md:max-w-[480px] aspect-[4/5] flex-shrink-0
        rounded-2xl cursor-pointer overflow-hidden group isolate
        transition-all duration-500
      `}
      style={{ transform: 'translateZ(0)', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
      onClick={onToggle}
    >
      {/* Background Gradient or Image */}
      {image ? (
        <>
          {/* Blur Placeholder - Always visible initially */}
          {/* Blur Placeholder - Always visible behind - NO FADE OUT to ensure 0ms gap */}
          {imageBlur && (
            <img
              src={imageBlur}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
            />
          )}

          {/* High-res Image - Fades in when loaded */}
          <img
            ref={imgRef}
            src={image}
            srcSet={imageMobile ? `${imageMobile} 960w, ${image} 1920w` : undefined}
            sizes="(max-width: 768px) 100vw, 1920px"
            alt=""
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)} // Force reveal on error so we don't see infinite blur
            className={`
              absolute inset-0 w-full h-full object-cover
              transition-transform duration-700 ease-out group-hover:scale-105 will-change-transform
              ${isOpen ? 'blur-sm scale-105' : ''}
              ${imageLoaded ? 'opacity-100' : 'opacity-0'}
            `}
          />
        </>
      ) : (
        <div
          className={`absolute inset-0 bg-gradient-to-br ${gradient} transition-all duration-700 ease-out group-hover:scale-105 will-change-transform ${isOpen ? 'blur-sm scale-105' : ''}`}
        />
      )}

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90" />

      {/* Content Layer */}
      <div className="relative z-10 flex flex-col w-full pointer-events-none">

        {/* Headline - stays in place */}
        <h3 className="text-3xl md:text-4xl font-normal tracking-[-0.03em] text-white leading-[1.15] whitespace-pre-line">
          {headline}
        </h3>

        {/* The Reveal: Body slides up from behind button */}
        <div
          className={`
            grid transition-[grid-template-rows] duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]
            ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
          `}
        >
          <div className="overflow-hidden">
            <p
              className={`
                pt-5 pb-2 text-lg md:text-xl text-white font-light leading-relaxed text-balance
                transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
              `}
            >
              {body}
            </p>
          </div>
        </div>

        {/* Title as Button (The Trigger) - Anchored at bottom, doesn't move */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`
            self-start pointer-events-auto mt-6
            inline-flex items-center h-[46px]
            text-lg font-normal tracking-wide leading-none
            px-5 rounded-full 
            border transition-colors duration-150
            border-white text-white hover:border-white/60 hover:text-white/60
          `}
        >
          <span>{title}</span>
          <div className="relative w-4 h-4 ml-2">
            {/* Plus icon - visible when collapsed */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className={`absolute inset-0 transition-all duration-300 ease-out ${isOpen ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'}`}
            >
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {/* Chevron icon - visible when expanded */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className={`absolute inset-0 transition-all duration-300 ease-out ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'}`}
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};

const benefitCards = [
  {
    title: 'Focus',
    headline: 'Access deep focus\non demand',
    body: 'Move into your most important work sharp and concentrated, for as long as you need.',
    gradient: 'from-[#0f172a] to-[#1e3a8a]', // Blue focus
    image: '/images/focus.webp',
    imageBlur: IMAGE_BLURS.focus,
    imageMobile: '/images/focus-mobile.webp'
  },
  {
    title: 'Reset',
    headline: 'Recalibrate your system\nin minutes',
    body: 'Return your mind and body to balance easily with short practices.',
    gradient: 'from-[#1c1917] to-[#57534e]', // Stone/Clean
    image: '/images/reset.webp',
    imageBlur: IMAGE_BLURS.reset,
    imageMobile: '/images/reset-mobile.webp'
  },
  {
    title: 'Performance',
    headline: 'Sustain your peak performance',
    body: 'Maintain stable, high-performance energy and deliver your best work consistently.',
    gradient: 'from-[#111827] to-[#374151]', // Sharp grey
    image: '/images/performance.webp',
    imageBlur: IMAGE_BLURS.performance,
    imageMobile: '/images/performance-mobile.webp'
  },
  {
    title: 'Stress',
    headline: 'Master your\nstress response',
    body: 'Decrease your stress levels to make better, future-proof decisions.',
    gradient: 'from-[#2e0505] to-[#7f1d1d]', // Red alert/calm
    image: '/images/stress.webp',
    imageBlur: IMAGE_BLURS.stress,
    imageMobile: '/images/stress-mobile.webp'
  },
  {
    title: 'Sleep',
    headline: 'Get deep,\nrestorative sleep',
    body: 'Get the deep rest you need to wake up ready for the day.',
    gradient: 'from-[#1a1c2c] to-[#4a192c]', // Deep purple/red for sleep
    image: '/images/sleep.webp',
    imageBlur: IMAGE_BLURS.sleep,
    imageMobile: '/images/sleep-mobile.webp'
  },
];

const SplitSection: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbOffset, setThumbOffset] = useState(0);
  const [openCardIndex, setOpenCardIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    // Accordion behavior: if clicking the same card, close it; otherwise open the new one
    setOpenCardIndex(openCardIndex === index ? null : index);
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    // Proportional width: how much of the total is visible
    const widthRatio = (clientWidth / scrollWidth) * 100;
    setThumbWidth(widthRatio);

    // Proportional offset: where the visible window starts
    const offsetRatio = (scrollLeft / scrollWidth) * 100;
    setThumbOffset(offsetRatio);

    // Progress percentage for button disabled states (0 to 100)
    const maxScroll = scrollWidth - clientWidth;
    const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
    setScrollProgress(progress);
  };

  const scrollMessages = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const firstCard = container.firstElementChild?.firstElementChild as HTMLElement;
    const scrollAmount = firstCard ? firstCard.offsetWidth + 24 : 504;

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll);
      handleScroll();
      return () => {
        el.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, []);

  return (
    <section
      id="benefits"
      className="relative w-full min-h-screen flex flex-col justify-center bg-canvas text-text-primary py-24 md:py-32 overflow-hidden"
      aria-labelledby="benefits-heading"
    >
      {/* Heading */}
      <div className="w-full px-6 md:px-24 lg:px-32">
        <h2
          id="benefits-heading"
          className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-text-primary mb-16 md:mb-24 leading-[1.1]"
        >
          Unlock your new baseline
        </h2>
      </div>

      {/* Benefit Cards - Full width horizontal scroll */}
      <div
        ref={scrollRef}
        className="w-full overflow-x-auto pb-4 hide-scrollbar scroll-smooth"
      >
        <div className="flex flex-row gap-6 pl-4 pr-6 md:px-24 lg:px-32 w-max">
          {benefitCards.map((card, index) => (
            <BenefitCard
              key={index}
              title={card.title}
              headline={card.headline}
              body={card.body}
              gradient={card.gradient}
              image={card.image}
              imageBlur={card.imageBlur}
              imageMobile={card.imageMobile}
              isOpen={openCardIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>

      {/* Controls: Centered Layout with Side Buttons */}
      <div className="w-full px-6 md:px-24 lg:px-32 mt-12 flex flex-row items-center justify-center gap-6 md:gap-8">

        {/* Left Button */}
        <button
          onClick={() => scrollMessages('left')}
          className={`
            w-12 h-12 flex items-center justify-center rounded-full
            transition-colors duration-150 hover:bg-text-primary/10 active:scale-95
            disabled:opacity-30 disabled:hover:bg-transparent
            flex-shrink-0
          `}
          disabled={scrollProgress <= 1}
          aria-label="Scroll Left"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Scroll Progress Bar - Proportional Floating Thumb */}
        <div className="w-[240px] md:w-[480px] h-[2px] bg-primary/40 rounded-full relative">
          <div
            className="absolute top-0 h-full bg-primary rounded-full"
            style={{
              left: `${thumbOffset}%`,
              width: `${thumbWidth}%`
            }}
          />
        </div>

        {/* Right Button */}
        <button
          onClick={() => scrollMessages('right')}
          className={`
            w-12 h-12 flex items-center justify-center rounded-full
            transition-colors duration-150 hover:bg-text-primary/10 active:scale-95
            disabled:opacity-30 disabled:hover:bg-transparent
            flex-shrink-0
          `}
          disabled={scrollProgress >= 99}
          aria-label="Scroll Right"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

      </div>
    </section>
  );
};

export default SplitSection;
